import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig, getJobPortalSession } from "../../../helpers/jobPortalAuth";

const METRIC_CONFIG = [
  { key: "profile", label: "Profile Complete", icon: "bi-person-check-fill", color: "#0d6efd", bg: "#e7f1ff" },
  { key: "applied", label: "Applied Jobs", icon: "bi-send-fill", color: "#6f42c1", bg: "#f0ebff" },
  { key: "shortlisted", label: "Shortlisted", icon: "bi-star-fill", color: "#fd7e14", bg: "#fff3e0" },
  { key: "saved", label: "Saved Jobs", icon: "bi-bookmark-fill", color: "#198754", bg: "#e6f4ed" },
  { key: "recommended", label: "Recommended", icon: "bi-lightning-fill", color: "#0dcaf0", bg: "#e0f8fc" },
];

const SkeletonMetric = () => (
  <div className="col placeholder-glow">
    <div className="rounded-4 p-4" style={{ background: "#f8f9fa" }}>
      <div className="placeholder rounded mb-3" style={{ width: 40, height: 40 }} />
      <div className="placeholder col-8 rounded mb-2" />
      <div className="placeholder col-5 rounded" style={{ height: 28 }} />
    </div>
  </div>
);

const SeekerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [applications, setApplications] = useState([]);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [savedJobsCount, setSavedJobsCount] = useState(0);
  const [subscription, setSubscription] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      await getJobPortalSession();
      const authConfig = await getJobPortalAuthConfig();
      const [profileResponse, applicationResponse, publicJobsResponse] = await Promise.all([
        axiosInstance.get("/job-portal/profile/me", authConfig),
        axiosInstance.get("/job-portal/applications/me?limit=100", authConfig),
        axiosInstance.get("/job-portal/jobs/public?limit=30"),
      ]);

      const seekerProfile = profileResponse.data?.data?.profile || null;
      const completeness = profileResponse.data?.data?.profileCompleteness || null;
      const applicationList = applicationResponse.data?.data?.applications || [];
      const jobs = publicJobsResponse.data?.data?.jobs || [];

      const preferredSkills = Array.isArray(seekerProfile?.functionalAreas) ? seekerProfile.functionalAreas : [];
      const appliedJobIds = new Set(applicationList.map((item) => item.jobId));
      const matched = jobs
        .filter((job) => !appliedJobIds.has(job.id))
        .filter((job) => {
          const skillMatch = job.skills?.some((s) => preferredSkills.includes(s));
          const locationMatch = job.location?.state && seekerProfile?.preferredLocation &&
            String(job.location.state).toLowerCase().includes(String(seekerProfile.preferredLocation).toLowerCase());
          return skillMatch || locationMatch;
        })
        .slice(0, 5);

      setProfile(seekerProfile);
      setProfileCompleteness(completeness);
      setApplications(applicationList);
      setRecommendedJobs(matched);
      setSavedJobsCount(profileResponse.data?.data?.counts?.savedJobs || 0);
      setSubscription(profileResponse.data?.data?.subscription || null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const metricValues = useMemo(() => ({
    profile: `${profileCompleteness?.percentage || 0}%`,
    applied: applications.length,
    shortlisted: applications.filter((a) => [2, 3, 4].includes(a.status)).length,
    saved: savedJobsCount,
    recommended: recommendedJobs.length,
  }), [applications, profileCompleteness, recommendedJobs.length, savedJobsCount]);

  const pct = profileCompleteness?.percentage || 0;
  const progressColor = pct >= 80 ? "#198754" : pct >= 50 ? "#fd7e14" : "#dc3545";

  return (
    <>
      <style>{`
        .dash-metric-card {
          border-radius: 16px;
          border: 1.5px solid transparent;
          padding: 20px;
          transition: transform 0.18s, box-shadow 0.18s;
          cursor: default;
        }
        .dash-metric-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important;
        }
        .dash-metric-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
          margin-bottom: 12px;
        }
        .dash-metric-val {
          font-size: 26px;
          font-weight: 700;
          line-height: 1;
          margin-bottom: 4px;
        }
        .dash-metric-label { font-size: 12px; color: #868e96; font-weight: 500; text-transform: uppercase; letter-spacing: 0.6px; }
        .section-card {
          border-radius: 16px;
          border: 1px solid #e9ecef;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05);
        }
        .section-card .card-body { padding: 24px; }
        .section-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 20px;
          padding-bottom: 14px;
          border-bottom: 1px solid #f1f3f5;
        }
        .section-title { font-size: 15px; font-weight: 700; margin: 0; }
        .profile-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f8f9fa; font-size: 13.5px; }
        .profile-row:last-child { border-bottom: none; }
        .profile-row-label { color: #868e96; font-weight: 500; }
        .profile-row-value { color: #212529; font-weight: 500; text-align: right; max-width: 60%; }
        .rec-job-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 12px 0; border-bottom: 1px solid #f1f3f5; gap: 12px;
        }
        .rec-job-row:last-child { border-bottom: none; }
        .rec-job-initials {
          width: 40px; height: 40px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700; flex-shrink: 0;
        }
        .missing-badge {
          display: inline-flex; align-items: center; gap: 4px;
          background: #fff3cd; color: #856404;
          border-radius: 20px; font-size: 11px; font-weight: 500;
          padding: 3px 10px; margin: 2px;
        }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />

        {/* Header */}
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 className="fw-bold mb-1" style={{ letterSpacing: "-0.3px" }}>
                <i className="bi bi-speedometer2 text-primary me-2" />
                Job Seeker Dashboard
              </h4>
              <p className="text-muted mb-0 small">
                Track your progress, applications, and discover new roles.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/job-seeker/plans" className="btn btn-outline-dark">
                <i className="bi bi-credit-card me-2" />My Plan
              </Link>
              <Link to="/job-seeker/profile" className="btn btn-outline-primary">
                <i className="bi bi-pencil me-2" />Update Profile
              </Link>
              <Link to="/job-seeker/jobs" className="btn btn-primary">
                <i className="bi bi-briefcase me-2" />Browse Jobs
              </Link>
            </div>
          </div>
        </div>

        {!loading && subscription?.status !== "active" && (
          <div className="alert alert-warning border-0 shadow-sm">
            Your plan is inactive. Buy a job seeker plan to apply for jobs.
          </div>
        )}

        {/* Metrics */}
        <div className="row g-3 mb-4">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonMetric key={i} />)
            : METRIC_CONFIG.map((m) => (
                <div className="col-md-6 col-xl" key={m.key}>
                  <div className="dash-metric-card shadow-sm" style={{ background: m.bg, borderColor: m.bg }}>
                    <div className="dash-metric-icon" style={{ background: `${m.color}1a`, color: m.color }}>
                      <i className={`bi ${m.icon}`} />
                    </div>
                    <div className="dash-metric-val" style={{ color: m.color }}>{metricValues[m.key]}</div>
                    <div className="dash-metric-label">{m.label}</div>
                  </div>
                </div>
              ))}
        </div>

        {!loading && (
          <div className="row g-4">
            {/* Profile Progress */}
            <div className="col-lg-5">
              <div className="card section-card border-0 h-100">
                <div className="card-body">
                  <div className="section-header pt-0">
                    <h2 className="section-title">
                      <i className="bi bi-person-circle text-primary me-2" />
                      Profile Progress
                    </h2>
                    <Link to="/job-seeker/profile" className="btn btn-sm btn-outline-primary" style={{ borderRadius: 8 }}>
                      <i className="bi bi-pencil me-1" />Edit
                    </Link>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="small fw-semibold" style={{ color: progressColor }}>{pct}% Complete</span>
                      <span className="small text-muted">{pct < 100 ? `${100 - pct}% remaining` : "All done!"}</span>
                    </div>
                    <div className="rounded-pill overflow-hidden" style={{ height: 10, background: "#f1f3f5" }}>
                      <div
                        className="rounded-pill"
                        style={{ width: `${pct}%`, height: "100%", background: progressColor, transition: "width 0.6s ease" }}
                      />
                    </div>
                  </div>

                  {/* Missing Sections */}
                  {profileCompleteness?.missingSections?.length > 0 && (
                    <div className="mb-3">
                      <div className="small text-muted mb-2">Complete these sections:</div>
                      <div>
                        {profileCompleteness.missingSections.map((s) => (
                          <span key={s} className="missing-badge">
                            <i className="bi bi-exclamation-circle" />{s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Profile Fields */}
                  <div>
                    {[
                      { label: "Preferred Location", value: profile?.preferredLocation },
                      { label: "Job Type", value: profile?.preferredJobType },
                      { label: "Salary Expectation", value: profile?.salaryExpectation },
                      { label: "LinkedIn", value: profile?.linkedInProfile },
                      { label: "Resume", value: profile?.resumeUrl ? "✅ Uploaded" : null },
                    ].map(({ label, value }) => (
                      <div className="profile-row" key={label}>
                        <span className="profile-row-label">{label}</span>
                        <span className="profile-row-value" style={{ color: value ? "#212529" : "#ced4da" }}>
                          {value || "Not added"}
                        </span>
                      </div>
                    ))}
                  </div>

                  {profile?.resumeUrl && (
                    <a
                      href={weburl + String(profile.resumeUrl).replace("public", "")}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-dark mt-3 w-100"
                      style={{ borderRadius: 8 }}
                    >
                      <i className="bi bi-file-earmark-person me-2" />View Resume
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Recommended Jobs */}
            <div className="col-lg-7">
              <div className="card section-card border-0 h-100">
                <div className="card-body">
                  <div className="section-header pt-0">
                    <h2 className="section-title">
                      <i className="bi bi-lightning-fill text-warning me-2" />
                      Recommended Jobs
                    </h2>
                    <Link to="/job-seeker/jobs" className="btn btn-sm btn-outline-primary" style={{ borderRadius: 8 }}>
                      Browse all
                    </Link>
                  </div>

                  {recommendedJobs.length === 0 ? (
                    <div className="text-center py-4">
                      <i className="bi bi-stars text-muted" style={{ fontSize: 40, opacity: 0.3 }} />
                      <p className="text-muted small mt-3 mb-0">
                        Update your skills and preferred location to get personalized recommendations.
                      </p>
                    </div>
                  ) : (
                    recommendedJobs.map((job) => {
                      const initials = (job.company?.name || "CO").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                      const colors = [["#e7f1ff","#0d6efd"],["#f0ebff","#6f42c1"],["#e6f4ed","#198754"],["#fff3e0","#fd7e14"],["#fce8f3","#c2185b"]];
                      const [cbg, cfg] = colors[(job.title?.charCodeAt(0) || 0) % colors.length];
                      return (
                        <div className="rec-job-row" key={job.id}>
                          <div className="d-flex gap-3 align-items-center">
                            <div className="rec-job-initials" style={{ background: cbg, color: cfg }}>{initials}</div>
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{job.title}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                <i className="bi bi-building me-1" />{job.company?.name || "Company"}
                                <span className="mx-2">·</span>
                                <i className="bi bi-geo-alt me-1" />{job.location?.display || "Location TBD"}
                              </div>
                              <div style={{ fontSize: 11, marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                {(job.skills || []).slice(0, 3).map((s) => (
                                  <span key={s} className="badge" style={{ background: "#f1f3f5", color: "#495057", fontWeight: 500, borderRadius: 6 }}>{s}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <Link to="/job-seeker/jobs" className="btn btn-sm btn-outline-primary flex-shrink-0" style={{ borderRadius: 8 }}>
                            View
                          </Link>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default SeekerDashboard;
